"use client";

import * as React from "react";
import {
  Box,
  Card,
  Callout,
  CheckboxGroup,
  Flex,
  ScrollArea,
  Text,
  VisuallyHidden,
  Separator,
} from "@radix-ui/themes";
import { type ReactNode, useState } from "react";
import { useUpdateUserRole } from "./api/user.js";
import { Dialog, Button, Select, Skeleton } from "./elements.js";
import { getBestName } from "./utils.js";
import {
  Member,
  MemberRole,
  RolesAndConfig403,
  RolesAndConfig404,
  useRolesAndConfig,
} from "../api/endpoint.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

interface EditUserRoleDialogProps extends Dialog.RootProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: Member;
  children?: ReactNode;
}

export function EditUserRoleDialog({
  children,
  user,
  ...props
}: EditUserRoleDialogProps) {
  const rolesAndConfigQuery = useRolesAndConfig({
    query: {
      initialData: { roles: [], multipleRolesEnabled: false },
    },
  });
  const { data, ...restQueryState } = rolesAndConfigQuery;
  const queryState = {
    error: restQueryState.error,
    isLoading: restQueryState.isLoading,
    isSuccess: restQueryState.isSuccess,
  };

  return (
    <Dialog.Root {...props}>
      {children && <Dialog.Trigger>{children}</Dialog.Trigger>}
      {data.multipleRolesEnabled ? (
        <EditMultipleUserRolesDialogContent
          user={user}
          roles={data.roles}
          queryState={queryState}
          onFormSubmitSuccess={() => props.onOpenChange(false)}
        />
      ) : (
        <EditSingleUserRoleDialogContent
          user={user}
          roles={data.roles}
          queryState={queryState}
          onFormSubmitSuccess={() => props.onOpenChange(false)}
        />
      )}
    </Dialog.Root>
  );
}

interface EditSingleUserRoleDialogContentProps {
  user: Member;
  roles: MemberRole[];
  queryState: {
    error: RolesAndConfig403 | RolesAndConfig404 | null;
    isLoading: boolean;
    isSuccess: boolean;
  };
  onFormSubmitSuccess: () => void;
}

export function EditSingleUserRoleDialogContent({
  user,
  roles,
  queryState,
  onFormSubmitSuccess,
}: EditSingleUserRoleDialogContentProps) {
  const translate = useTranslation();
  const displayName = getBestName(user) || user.email;
  const updateUser = useUpdateUserRole();
  const [selectedRole, setSelectedRole] = useState(
    user.roles?.at(0)?.slug || "Unknown",
  );

  const onSubmitForm = ({ id, roles }: { id: string; roles: string[] }) => {
    updateUser.mutate(
      { userId: id, data: { roles } },
      {
        onSuccess: () => {
          onFormSubmitSuccess();
        },
      },
    );
  };

  const rootId = React.useId();
  const formId = `edit-user-form-${rootId}`;
  const selectId = `role-select-${rootId}`;
  const selectLabelId = `${selectId}-label`;
  const infoId = `${selectId}-info`;
  const errorId = `${selectId}-error`;
  const showErrorMessage = !!queryState.error;
  const showSingleRoleInfo = queryState.isSuccess && roles.length === 1;

  return (
    <Dialog.Content maxWidth="480px">
      <Dialog.Title>
        <Translation
          defaultMessage="Edit role"
          id="Ue7G8Y"
          description="Dialog title for editing user role"
        />
      </Dialog.Title>
      <Dialog.Description>
        <Translation
          defaultMessage="Select the role to assign to {displayName}"
          id="jWl49/"
          description="Dialog description for role selection"
          values={{ displayName: <Text weight="bold">{displayName}</Text> }}
        />
      </Dialog.Description>
      <Flex mt="2" direction="column" gap="1" asChild>
        <form
          id={formId}
          onSubmit={async (event) => {
            event.preventDefault();
            onSubmitForm({ id: user.id, roles: [selectedRole] });
          }}
        >
          <Select.Root
            name="roles"
            value={selectedRole ?? "Unknown"}
            onValueChange={setSelectedRole}
            disabled={queryState.isLoading || showSingleRoleInfo}
          >
            <Skeleton loading={queryState.isLoading}>
              <Select.Trigger
                id={selectId}
                placeholder={translate({
                  defaultMessage: "Assign a role",
                  id: "0beebz",
                  description: "Placeholder for role selection dropdown",
                })}
                aria-labelledby={selectLabelId}
                aria-invalid={showErrorMessage || undefined}
                aria-describedby={
                  [showErrorMessage && errorId, showSingleRoleInfo && infoId]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
              />
            </Skeleton>

            <Select.Content>
              {roles.map((role) => (
                <Select.Item key={role.slug} value={role.slug}>
                  {role.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>

          {showErrorMessage ? (
            <Text color="red" size="2" id={errorId}>
              {getRoleSelectErrorMessage(queryState.error)}
            </Text>
          ) : null}

          {showSingleRoleInfo ? (
            <Text color="gray" size="2" id={infoId} mt="1">
              <Translation
                defaultMessage="You cannot update the role for this user as there is only one role available."
                id="XLp48i"
                description="Info message when only one role is available"
              />
            </Text>
          ) : null}
        </form>
      </Flex>

      {updateUser.error ? (
        <Callout.Root color="red" mt="4" mb="-2">
          <Callout.Text>
            {getMutationErrorMessage(updateUser.error)}
          </Callout.Text>
        </Callout.Root>
      ) : null}

      <Flex mt="5" gap="3" justify="end">
        <Dialog.Close>
          <Button variant="secondary" disabled={updateUser.isPending}>
            <Translation
              defaultMessage="Cancel"
              id="hHNj31"
              description="Cancel button text"
            />
          </Button>
        </Dialog.Close>

        <Button
          form={formId}
          loading={updateUser.isPending}
          disabled={queryState.isLoading || showSingleRoleInfo || undefined}
        >
          <Translation
            defaultMessage="Save"
            id="RT8KNi"
            description="Save button text"
          />
        </Button>
      </Flex>
      {/* mirror errors in a live region */}
      <VisuallyHidden asChild>
        <section aria-live="polite">
          {getMutationErrorMessage(updateUser.error)}
        </section>
      </VisuallyHidden>
    </Dialog.Content>
  );
}

type EditMultipleUserRolesDialogContentProps =
  EditSingleUserRoleDialogContentProps;

function EditMultipleUserRolesDialogContent({
  user,
  roles,
  queryState,
  onFormSubmitSuccess,
}: EditMultipleUserRolesDialogContentProps) {
  const displayName = getBestName(user) || user.email;
  const updateUser = useUpdateUserRole();
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (queryState.error && !error) {
      setError("There was an error fetching roles. Please try again.");
    }
  }, [queryState.error, error]);

  const onSubmitForm = ({ id, roles }: { id: string; roles: string[] }) => {
    if (roles.length === 0) {
      setError("Please select at least one role");
      return;
    }
    updateUser.mutate(
      { userId: id, data: { roles } },
      {
        onSuccess: () => {
          onFormSubmitSuccess();
        },
        onError: () => {
          setError(
            "There was an error updating the user role. Please try again.",
          );
        },
      },
    );
  };

  const rootId = React.useId();
  const formId = `edit-user-form-${rootId}`;
  const checkboxGroupId = `role-checkbox-group-${rootId}`;
  const infoId = `${checkboxGroupId}-info`;
  const showSingleRoleInfo = queryState.isSuccess && roles.length === 1;
  return (
    <Dialog.Content maxWidth="480px">
      <Flex
        direction="column"
        gap="1"
        maxHeight="calc(100vh - var(--dialog-content-padding) * 2 - var(--space-6) - max(var(--space-6), 6vh)"
        minHeight="200px"
      >
        <Dialog.Title>
          <Translation
            defaultMessage="Edit roles"
            id="VQ1Avr"
            description="Dialog title for editing user roles (multiple)"
          />
        </Dialog.Title>
        <Dialog.Description>
          <Translation
            defaultMessage="Select the roles to assign to {displayName}"
            id="yyM/+B"
            description="Dialog description for selecting multiple roles"
            values={{ displayName: <Text weight="bold">{displayName}</Text> }}
          />
        </Dialog.Description>
        <Flex mt="4" direction="column" gap="1" asChild minHeight="0">
          <Card
            asChild
            style={{ display: "flex", padding: "2px 0", flexGrow: 0 }}
          >
            <form
              id={formId}
              onSubmit={async (event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const selectedRoles = formData
                  .getAll("roles")
                  .map((role) => role.toString());
                onSubmitForm({ id: user.id, roles: selectedRoles });
              }}
            >
              <ScrollArea>
                <CheckboxGroup.Root
                  defaultValue={user.roles?.map((role) => role.slug) || []}
                  name="roles"
                  style={{ gap: "0" }}
                  id={checkboxGroupId}
                  onValueChange={(values) => {
                    if (error && values.length > 0) {
                      setError("");
                    }
                  }}
                >
                  {roles.map((role, i) => (
                    <Box key={role.slug}>
                      <Flex px="4" justify="center" py="3">
                        <CheckboxGroup.Item
                          value={role.slug}
                          style={{
                            width: "100%",
                            gap: "var(--space-3)",
                            alignItems: "center",
                          }}
                          disabled={showSingleRoleInfo}
                        >
                          <Flex direction="column" width="100%">
                            <Text weight="bold" size="2">
                              {role.name}
                            </Text>
                            <Text
                              color="gray"
                              size="2"
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {role.description}
                            </Text>
                          </Flex>
                        </CheckboxGroup.Item>
                      </Flex>
                      {i !== roles.length - 1 && (
                        <Separator orientation="horizontal" size="4" />
                      )}
                    </Box>
                  ))}
                </CheckboxGroup.Root>
              </ScrollArea>
            </form>
          </Card>
        </Flex>

        {showSingleRoleInfo ? (
          <Text color="gray" size="2" id={infoId} mt="1">
            <Translation
              defaultMessage="You cannot update the role for this user as there is only one role available."
              id="XLp48i"
              description="Info message when only one role is available"
            />
          </Text>
        ) : null}

        {error ? (
          <Callout.Root color="red" mt="4" mb="-2">
            <Callout.Text>{error}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex mt="5" gap="3" justify="end">
          <Dialog.Close>
            <Button variant="secondary" disabled={updateUser.isPending}>
              <Translation
                defaultMessage="Cancel"
                id="hHNj31"
                description="Cancel button text"
              />
            </Button>
          </Dialog.Close>

          <Button
            form={formId}
            loading={updateUser.isPending}
            disabled={queryState.isLoading || showSingleRoleInfo || undefined}
          >
            <Translation
              defaultMessage="Save"
              id="RT8KNi"
              description="Save button text"
            />
          </Button>
        </Flex>
      </Flex>
      {/* mirror errors in a live region */}
      <VisuallyHidden asChild>
        <section aria-live="polite">
          {getMutationErrorMessage(updateUser.error)}
        </section>
      </VisuallyHidden>
    </Dialog.Content>
  );
}

function getRoleSelectErrorMessage(error: unknown) {
  if (!error) {
    return null;
  }
  // TODO Handle server errors
  return "There was an error fetching roles. Please try again.";
}

function getMutationErrorMessage(error: unknown) {
  if (!error) {
    return null;
  }
  // TODO Handle server errors
  return "There was an error updating the user role. Please try again.";
}

// Note: Error messages in helper functions are kept as plain strings
// since they are displayed dynamically
